/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

/**
 * Compresses and resizes a base64 image string to fit signature dimensions.
 * Ensures the payload is incredibly lightweight (<15KB) and preserves transparency.
 */
export function compressImage(
  base64Str: string,
  maxWidth: number = 300,
  maxHeight: number = 100
): Promise<string> {
  return new Promise((resolve) => {
    if (!base64Str || typeof window === 'undefined') {
      resolve(base64Str);
      return;
    }

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = base64Str;
    
    img.onload = () => {
      let width = img.width;
      let height = img.height;

      // Calculate optimized scaling ratio
      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      if (ctx) {
        // Draw image onto the optimized canvas size
        ctx.clearRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        
        // Export to standard compressed PNG to preserve alpha channel transparency
        const compressedBase64 = canvas.toDataURL('image/png');
        resolve(compressedBase64);
      } else {
        resolve(base64Str);
      }
    };

    img.onerror = (err) => {
      console.warn('Error loading image for client-side compression fallback:', err);
      resolve(base64Str);
    };
  });
}
